import { type FC, type FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/Button/Button';
import { Input } from '@/components/Input/Input';
import { HERO_SIZE, type HeroKey, heroSrc } from '@/components/PageHero/heroImages';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { WHATS_NEW_CARDS, WHATS_NEW_TOWN, WHATS_NEW_VERSION } from '@/content/whatsNew';
import { setHome } from '@/services/nearMe';
import { markWhatsNewSeen } from '@/services/whatsNew';
import { useAuthStore } from '@/stores/authStore';
import { notifyHomeAreaChanged } from '@/utils/homeAreaEvents';

export interface WhatsNewModalProps {
  /** Other modals, the wizard and live screens come first: pass false while any of them is up. */
  canShow: boolean;
}

const Scene: FC<{ heroKey: HeroKey }> = ({ heroKey }) => (
  <picture aria-hidden="true">
    {(['avif', 'webp'] as const).map((format) => (
      <source key={format} type={`image/${format}`} srcSet={`${heroSrc(heroKey, 'sm', format)} 800w, ${heroSrc(heroKey, 'lg', format)} ${HERO_SIZE.width}w`} sizes="(min-width: 640px) 36rem, 100vw" />
    ))}
    <img
      src={heroSrc(heroKey, 'sm', 'webp')}
      alt=""
      width={HERO_SIZE.width}
      height={HERO_SIZE.height}
      decoding="async"
      style={{ filter: 'var(--hero-image-filter)' }}
      className="h-32 w-full rounded-md border border-border-subtle object-cover sm:h-44"
    />
  </picture>
);

/**
 * WhatsNewModal - the one-time release pop-up: a few highlight cards, then an optional
 * "Where do you play?" step that reuses the home area setting. Any way of closing it
 * (Skip, Close, Escape, Done) records the release as seen on the account.
 */
export const WhatsNewModal: FC<WhatsNewModalProps> = ({ canShow }) => {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);
  const [town, setTown] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const closing = useRef(false);

  const total = WHATS_NEW_CARDS.length + 1;
  const last = step === total - 1;
  const card = last ? null : WHATS_NEW_CARDS[step];
  const visible = canShow && open;

  const close = () => {
    if (closing.current) return;
    closing.current = true;
    setOpen(false);
    // Remember it locally first so it cannot come back this session, then on the account
    if (user) setUser({ ...user, whats_new_seen_version: WHATS_NEW_VERSION });
    void markWhatsNewSeen(WHATS_NEW_VERSION);
  };

  useDialogFocus({ isOpen: visible, containerRef: panelRef, onClose: close });

  // Each new card is announced by moving focus to its heading
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  if (!visible) return null;

  const saveTown = async (ev: FormEvent) => {
    ev.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const { data, error: err } = await setHome(town.trim());
    setBusy(false);
    if (err || !data) {
      setError(err?.message ?? 'We could not find that place. Try a nearby town or a postcode, or skip for now.');
      return;
    }
    setSaved(data.label ?? town.trim());
    notifyHomeAreaChanged();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-bg-void/70" aria-hidden="true" onClick={close} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whats-new-title"
        aria-describedby="whats-new-progress"
        className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-lg border border-border-default bg-bg-secondary p-4 shadow-xl focus:outline-none sm:max-w-xl sm:rounded-lg sm:p-5"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-accent-primary">What's new</p>
            <p id="whats-new-progress" className="text-sm text-text-muted">Step {step + 1} of {total}</p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close what's new"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-text-muted hover:bg-bg-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div aria-live="polite">
          <Scene heroKey={card ? card.hero : WHATS_NEW_TOWN.hero} />
          <h2 id="whats-new-title" ref={headingRef} tabIndex={-1} className="mt-4 text-2xl focus:outline-none">
            {card ? card.title : WHATS_NEW_TOWN.title}
          </h2>
          <p className="mt-2 text-text-secondary">{card ? card.body : WHATS_NEW_TOWN.body}</p>
        </div>

        {card && card.to && card.linkLabel && (
          <p className="mt-3">
            <Link to={card.to} onClick={close} className="inline-flex min-h-[44px] items-center text-accent-primary underline">
              {card.linkLabel}
            </Link>
          </p>
        )}

        {last && (
          saved ? (
            <p role="status" className="mt-4 rounded border border-semantic-success/40 bg-semantic-success/10 px-3 py-2 text-sm text-text-primary">
              Saved: {saved}. Home and Near Me will use it from now on.
            </p>
          ) : (
            <form onSubmit={saveTown} className="mt-4 space-y-3">
              <Input
                label={`${WHATS_NEW_TOWN.question} (optional)`}
                value={town}
                maxLength={120}
                autoComplete="off"
                onChange={(e) => setTown(e.target.value)}
                hint="Your nearest town or city, or a postcode."
              />
              <p className="text-sm text-text-secondary">{WHATS_NEW_TOWN.privacy}</p>
              {error && <p role="alert" className="text-sm text-error">{error}</p>}
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={town.trim().length < 2 || busy} isLoading={busy} loadingText="Saving">
                  Save my town
                </Button>
                <Button type="button" variant="ghost" onClick={close}>Skip for now</Button>
              </div>
            </form>
          )
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle pt-4">
          <Button type="button" variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={step === 0}>Back</Button>
          {last ? (
            saved ? <Button type="button" onClick={close}>Done</Button> : <span />
          ) : (
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={close}>Skip</Button>
              <Button type="button" onClick={() => setStep((s) => s + 1)}>Next</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
